import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  CheckCircle,
  Clock,
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Zap,
  Shield,
  ArrowRight,
  Store,
  ShoppingCart,
  CreditCard,
  Calendar,
  MapPin,
  Building
} from 'lucide-react';

const RetailIndustryPage: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-purple-800/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <ShoppingBag className="h-8 w-8 text-purple-600 mr-3" />
                <span className="text-sm font-semibold text-purple-600 uppercase tracking-wider">Retail</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Streamline Your Retail Operations
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Manage store maintenance, equipment repairs, and facility operations for retail chains, shopping centers, and restaurants with WorkOrder HQ™. 
                From POS systems to HVAC maintenance, keep your retail business running smoothly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup?industry=3"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-purple-600 bg-white dark:bg-gray-800 border-2 border-purple-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                >
                  View Pricing
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                14-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center">
                    <Store className="h-8 w-8 text-purple-600 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Store Maintenance</p>
                      <p className="text-gray-600 dark:text-gray-400">Fixtures, lighting & displays</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <ShoppingCart className="h-8 w-8 text-purple-500 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">POS Systems</p>
                      <p className="text-gray-600 dark:text-gray-400">Point of sale & payment systems</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <CreditCard className="h-8 w-8 text-purple-700 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Equipment</p>
                      <p className="text-gray-600 dark:text-gray-400">Refrigeration & security systems</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Retail-Specific Features */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Built for Retail Businesses
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Everything you need to manage your retail operations efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 dark:bg-purple-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Off-Hours Maintenance
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Schedule maintenance during off-hours to minimize customer disruption and maintain sales.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <ShoppingCart className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                POS System Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track and maintain point-of-sale systems, payment terminals, and cash registers.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Building className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Multi-Location Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Manage maintenance across multiple store locations, shopping centers, and franchises.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-orange-100 dark:bg-orange-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Emergency Repairs
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Handle emergency repairs for critical systems like refrigeration, security, and lighting.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-red-100 dark:bg-red-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Security Systems
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Maintain security cameras, alarms, and access control systems to protect inventory and staff.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-indigo-100 dark:bg-indigo-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Cost Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track maintenance costs, equipment ROI, and operational expenses across all locations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Common Retail Use Cases
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              See how retail businesses use WorkOrder HQ™ to streamline operations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Use Case 1 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <Store className="h-8 w-8 text-purple-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Retail Stores</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Manage store maintenance, POS systems, and equipment for individual retail locations and chains.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Store fixtures & display maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  POS & payment system support
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Lighting & HVAC maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Security system maintenance
                </li>
              </ul>
            </div>

            {/* Use Case 2 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <ShoppingBag className="h-8 w-8 text-purple-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Shopping Centers</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Handle large-scale mall operations including common areas, food courts, and multi-tenant facilities.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Common area maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Food court equipment maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Parking lot & exterior maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Tenant coordination & support
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Why Retail Businesses Choose WorkOrder HQ™
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Minimize Customer Disruption
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Schedule maintenance during off-hours to keep stores open and customers shopping.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Protect Revenue Streams
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Keep POS systems and critical equipment running to maintain sales and customer service.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Ensure Store Safety
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Maintain security systems and safety equipment to protect customers and staff.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Scale Operations
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Manage maintenance across multiple locations efficiently as your business grows.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-8 text-white">
              <div className="space-y-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">14 Days</p>
                    <p className="text-purple-100">Free Trial</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Up to 10</p>
                    <p className="text-purple-100">Technicians</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FileText className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Unlimited</p>
                    <p className="text-purple-100">Work Orders</p>
                  </div>
                </div>
                <Link
                  to="/signup?industry=3"
                  className="block w-full bg-white text-purple-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors mt-6"
                >
                  Get Started Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Retail Operations?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join retail businesses who trust WorkOrder HQ™ to streamline their operations and maintain customer satisfaction.
          </p>
          <Link
            to="/signup?industry=3"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-purple-600 bg-white rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
          <p className="mt-4 text-sm text-purple-100">
            No credit card required • Full access to all features
          </p>
        </div>
      </section>
    </div>
  );
};

export default RetailIndustryPage;
